import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';

interface BubbleHeatmapProps {
  data: { hour: number; count: number }[];
  height?: number;
  timezone?: string;
}

function getTimezoneOffsetHours(timezone: string): number {
  try {
    const now = new Date();
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC', hour: 'numeric', hour12: false });
    const tzStr = now.toLocaleString('en-US', { timeZone: timezone, hour: 'numeric', hour12: false });
    const diff = (parseInt(tzStr) - parseInt(utcStr) + 24) % 24;
    return diff > 12 ? diff - 24 : diff;
  } catch {
    return 0;
  }
}

export function BubbleHeatmapChart({ data, height = 200, timezone = 'UTC' }: BubbleHeatmapProps) {
  const colors = useChartColors();
  const offsetHours = getTimezoneOffsetHours(timezone);
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  // Build full 24-hour data, shifting UTC hours to local timezone
  const hourMap = new Map(data.map((d) => [d.hour, d.count]));
  const chartData = Array.from({ length: 24 }, (_, localHour) => {
    const utcHour = ((localHour - offsetHours) % 24 + 24) % 24;
    const count = hourMap.get(utcHour) || 0;
    return {
      x: localHour,
      y: 1,
      z: count,
      hour: localHour,
      count,
      opacity: count > 0 ? Math.max(0.25, count / maxCount) : 0.05,
    };
  });

  const formatHour = (h: number) => {
    if (h === 0) return '12am';
    if (h < 12) return `${h}am`;
    if (h === 12) return '12pm';
    return `${h - 12}pm`;
  };

  const busiest = chartData.reduce((a, b) => (b.count > a.count ? b : a), chartData[0]);

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
          <XAxis
            type="number"
            dataKey="x"
            domain={[0, 23]}
            ticks={[0, 3, 6, 9, 12, 15, 18, 21]}
            tickFormatter={(h) => formatHour(h)}
            stroke={colors.axis}
            fontSize={11}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="number"
            dataKey="y"
            hide
          />
          <ZAxis
            type="number"
            dataKey="z"
            range={[40, 600]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.tooltipBg,
              border: `1px solid ${colors.tooltipBorder}`,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              color: colors.text,
            }}
            content={({ active, payload }) => {
              if (active && payload && payload.length > 0) {
                const d = payload[0].payload;
                return (
                  <div
                    style={{
                      backgroundColor: colors.tooltipBg,
                      border: `1px solid ${colors.tooltipBorder}`,
                      borderRadius: '12px',
                      padding: '8px 12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                      color: colors.text,
                    }}
                  >
                    <p className="text-sm font-medium">{formatHour(d.hour)}</p>
                    <p className="text-sm">{d.count.toLocaleString()} viewers</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Scatter data={chartData} animationDuration={750}>
            {chartData.map((entry, index) => (
              <Cell
                key={index}
                fill={`rgba(${colors.heatmapRgb}, ${entry.opacity})`}
                stroke={`rgba(${colors.heatmapRgb}, ${Math.min(1, entry.opacity + 0.2)})`}
                strokeWidth={1}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      {data.length > 0 && (
        <div className="flex gap-4 mt-1 text-xs text-gray-500 dark:text-slate-400 px-2">
          <span>
            Busiest: <span className="font-medium text-gray-700 dark:text-slate-200">{formatHour(busiest.hour)}</span> ({busiest.count.toLocaleString()})
          </span>
        </div>
      )}
    </div>
  );
}
